import * as React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useZustand } from '../../hooks/useZustand'

export default function Tags({ onTagsChange, selectedTags, noLabel }) {
    const availableTags = Array.from(useZustand.getState().tags || new Set())

    const [selectedLocalTags, setLocalSelectedTags] = React.useState([])

    React.useEffect(() => {
        setLocalSelectedTags(selectedTags || [])
    }, [selectedTags])

    const [inputValue, setInputValue] = React.useState('')
    const [autocompleteOpen, setAutocompleteOpen] = React.useState(false)

    const handleChange = (event, newValue) => {
        // Filter out any null or undefined values
        const validValues = newValue.filter(Boolean)
        onTagsChange(validValues)
        setLocalSelectedTags(validValues)
        useZustand.getState().addNewTag(inputValue)
    }

    const getFilterOptions = (options, params) => {
        const filtered = options.filter(option =>
            option.toLowerCase().startsWith(params.inputValue.toLowerCase())
        )

        // Add the "create" option if input is not empty and doesn't exist in options
        if (
            params.inputValue !== '' &&
            !options.includes(params.inputValue) &&
            !filtered.includes(params.inputValue)
        ) {
            filtered.push(params.inputValue)
        }

        return filtered
    }

    return (
        <Autocomplete
            multiple
            variant="standard"
            id="tags-autocomplete"
            value={selectedLocalTags}
            onChange={handleChange}
            inputValue={inputValue}
            autoHighlight
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue)
            }}
            onKeyDown={event => {
                // stop the event from bubbling up to the parent component
                // first save the tag then save the statement
                if (inputValue !== '' || autocompleteOpen)
                    event.stopPropagation()

                const options = getFilterOptions(availableTags, {
                    inputValue: inputValue,
                })
                // if there is only option to create
                // let user press enter to save new category
                if (event.key === 'Enter' && options.length === 1) {
                    event.defaultMuiPrevented = true
                    handleChange(event, [...selectedLocalTags, ...options])
                    setInputValue('')
                }
            }}
            options={availableTags}
            filterOptions={getFilterOptions}
            getOptionLabel={option => option}
            renderOption={(props, option) => {
                const isNewOption = !availableTags.includes(option)
                return (
                    <li {...props}>
                        {isNewOption ? (
                            <Box>
                                <Typography>Create new tag: </Typography>
                                <Typography component="span">
                                    "{option}"
                                </Typography>
                            </Box>
                        ) : (
                            option
                        )}
                    </li>
                )
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        label={option}
                        key={`${index}-${option}`}
                        size="small"
                        sx={{
                            color: theme => theme.palette.colors.chip,
                            height: '20px',
                            fontSize: '12px',
                        }}
                        {...getTagProps({ index })}
                        // sx={{ m: 0.5 }}
                    />
                ))
            }
            renderInput={params => (
                <TextField
                    {...params}
                    label={noLabel ? '' : 'tags for this statement'}
                    placeholder="add tags, separate with enter"
                    size="small"
                    variant="standard"
                    inputProps={{
                        style: {
                            fontSize: '12px',
                            marginBottom: '4px',
                            lineHeight: '1',
                        },
                        ...params.inputProps,
                    }}
                    InputLabelProps={{
                        style: {
                            fontSize: '12px',
                        },
                    }}
                    sx={{
                        width: '100%',
                        my: 0,
                        fontSize: '12px',
                        lineHeight: '1.5',
                        color: '#37352F',
                    }}
                />
            )}
            PaperComponent={({ children, ...other }) => (
                <Paper
                    {...other}
                    sx={{
                        width: '224px',
                        overflowY: 'auto',
                        fontSize: '12px',
                        lineHeight: '1.5',
                    }}
                >
                    {children}
                </Paper>
            )}
            sx={{
                my: 3,
                fontSize: '12px',
                width: '100%',
                '& .MuiOutlinedInput-root': {
                    padding: 0,
                    margin: 0,
                    fontSize: '12px',
                },
                '& .MuiOutlinedInput-root::before': {
                    borderBottom: '0px',
                },
            }}
        />
    )
}
